import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';

let uiTextareaUid = 0;

export type UiTextareaResize = 'none' | 'vertical' | 'auto';

/**
 * Átomo: campo de texto multilínea.
 * Comparte tokens visuales con ui-input. Sin HTTP ni stores.
 */
@Component({
  selector: 'ui-textarea',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './textarea.component.html',
  styleUrl: './textarea.component.scss',
  host: {
    class: 'ui-textarea-host',
    '[class.ui-textarea-host--block]': 'block()',
  },
})
export class UiTextareaComponent implements OnInit, OnDestroy {
  @ViewChild('textareaEl') private textareaEl?: ElementRef<HTMLTextAreaElement>;

  readonly value = model<string>('');
  readonly label = input('');
  readonly placeholder = input('');

  readonly required = input(false);
  readonly showOptionalHint = input(true);
  readonly requiredHint = input('Obligatorio');
  readonly optionalHint = input('Opcional');

  readonly disabled = input(false);
  readonly readonly = input(false);
  readonly invalid = input(false);
  readonly message = input<string | undefined>(undefined);
  readonly showMessage = input(true);

  readonly name = input<string | undefined>(undefined);
  readonly autocomplete = input<string | undefined>(undefined);
  readonly maxlength = input<number | undefined>(undefined);
  readonly minlength = input<number | undefined>(undefined);

  /**
   * Número de filas visibles cuando resize !== 'auto'.
   * En modo 'auto' actúa como altura mínima.
   */
  readonly rows = input(3);

  /**
   * - 'none'     → sin redimensionado.
   * - 'vertical' → el usuario puede redimensionar manualmente.
   * - 'auto'     → crece automáticamente con el contenido.
   */
  readonly resize = input<UiTextareaResize>('vertical');

  /**
   * Muestra contador de caracteres cuando se define maxlength.
   */
  readonly showCounter = input(true);

  readonly block = input(false);
  readonly fieldId = input<string | undefined>(undefined);

  readonly valueChange = output<string>();
  readonly blurred = output<FocusEvent>();
  readonly focused = output<FocusEvent>();

  protected readonly generatedId = `ui-textarea-${++uiTextareaUid}`;
  protected readonly inputId = computed(() => this.fieldId() ?? this.generatedId);
  protected readonly messageId = computed(() => `${this.inputId()}-message`);

  protected readonly charCount = computed(() => this.value().length);

  protected readonly counterExceeded = computed(() => {
    const max = this.maxlength();
    return max !== undefined && this.charCount() > max;
  });

  protected readonly showRequirement = computed(
    () => this.required() || this.showOptionalHint(),
  );

  protected readonly requirementText = computed(() =>
    this.required() ? this.requiredHint() : this.optionalHint(),
  );

  protected readonly ariaDescribedBy = computed(() => {
    const parts: string[] = [];
    if (this.message()) parts.push(this.messageId());
    if (this.maxlength() !== undefined && this.showCounter()) {
      parts.push(`${this.inputId()}-counter`);
    }
    return parts.length ? parts.join(' ') : null;
  });

  /** Para auto-resize: almacena la altura forzada. */
  protected readonly autoHeight = signal<string | null>(null);

  ngOnInit(): void {
    if (this.resize() === 'auto') {
      this.recalcHeight();
    }
  }

  ngOnDestroy(): void {/* noop */}

  protected onInput(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    this.value.set(el.value);
    this.valueChange.emit(el.value);

    if (this.resize() === 'auto') {
      this.recalcHeight();
    }
  }

  protected onBlur(event: FocusEvent): void {
    this.blurred.emit(event);
  }

  protected onFocus(event: FocusEvent): void {
    this.focused.emit(event);
  }

  private recalcHeight(): void {
    const el = this.textareaEl?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    this.autoHeight.set(`${el.scrollHeight}px`);
  }
}
